import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate } from 'react-router-dom';
import {
  getTiebreakTypes,
  getTournamentTiebreaks,
  calculateStandings,
  saveStandings,
} from '../../../services/tournament.service';
import { TIEBREAK_TIPO_TORNEO } from '../../../constants/tournament.constants';

export default function StepStandings({ torId, onBack, setAlertInfo }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [standingsLoading, setStandingsLoading] = useState(false);
  const [dbTiebreaks, setDbTiebreaks] = useState([]);
  const [selectedTiebreaks, setSelectedTiebreaks] = useState([]); // Array of strings (names)
  const [standings, setStandings] = useState([]);

  // Load available tiebreak types and current tournament standings
  useEffect(() => {
    const initializeStep = async () => {
      if (!torId) return;
      setStandingsLoading(true);
      try {
        // 1. Fetch available tiebreak types
        const types = await getTiebreakTypes(TIEBREAK_TIPO_TORNEO.INDIVIDUAL);
        setDbTiebreaks(types || []);

        // 2. Fetch tournament's saved tiebreaks config
        const savedConfigs = await getTournamentTiebreaks(torId);
        const savedNames = savedConfigs
          .sort((a, b) => a.tpt_orden - b.tpt_orden)
          .map((c) => c.ttb_nombre);
        setSelectedTiebreaks(savedNames);

        // 3. Compute initial standings
        const initialStandings = await calculateStandings(torId, savedNames);
        setStandings(initialStandings || []);
      } catch (error) {
        console.error('Error initializing standings step:', error);
        setAlertInfo({
          open: true,
          message: 'Error al inicializar la clasificación y desempates.',
          severity: 'error',
        });
      } finally {
        setStandingsLoading(false);
      }
    };

    initializeStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [torId]);

  // Recalculate standings based on selected tiebreaks
  const handleApplyTiebreaks = async () => {
    if (!torId) return;
    setStandingsLoading(true);
    try {
      const updatedStandings = await calculateStandings(torId, selectedTiebreaks);
      setStandings(updatedStandings || []);
      setAlertInfo({
        open: true,
        message: 'Desempates aplicados correctamente.',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error calculating standings:', error);
      setAlertInfo({
        open: true,
        message: error.message || 'Error al calcular la clasificación.',
        severity: 'error',
      });
    } finally {
      setStandingsLoading(false);
    }
  };

  // Add a tiebreak to selected list
  const handleAddTiebreak = (name) => {
    if (selectedTiebreaks.includes(name)) return;
    setSelectedTiebreaks([...selectedTiebreaks, name]);
  };

  // Remove a tiebreak from selected list
  const handleRemoveTiebreak = (name) => {
    setSelectedTiebreaks(selectedTiebreaks.filter((t) => t !== name));
  };

  // Move a tiebreak up in priority
  const handleMoveTiebreakUp = (index) => {
    if (index === 0) return;
    const updated = [...selectedTiebreaks];
    const temp = updated[index];
    updated[index] = updated[index - 1];
    updated[index - 1] = temp;
    setSelectedTiebreaks(updated);
  };

  // Move a tiebreak down in priority
  const handleMoveTiebreakDown = (index) => {
    if (index === selectedTiebreaks.length - 1) return;
    const updated = [...selectedTiebreaks];
    const temp = updated[index];
    updated[index] = updated[index + 1];
    updated[index + 1] = temp;
    setSelectedTiebreaks(updated);
  };

  // Manual reordering of players in standings (Frontend only)
  const handleMovePlayerUp = (index) => {
    if (index === 0) return;
    const updated = [...standings];
    const temp = updated[index];
    updated[index] = updated[index - 1];
    updated[index - 1] = temp;

    // Re-assign positions based on new order
    const reordered = updated.map((player, idx) => ({
      ...player,
      posicionFinal: idx + 1,
    }));
    setStandings(reordered);
  };

  const handleMovePlayerDown = (index) => {
    if (index === standings.length - 1) return;
    const updated = [...standings];
    const temp = updated[index];
    updated[index] = updated[index + 1];
    updated[index + 1] = temp;

    // Re-assign positions based on new order
    const reordered = updated.map((player, idx) => ({
      ...player,
      posicionFinal: idx + 1,
    }));
    setStandings(reordered);
  };

  // Save standing positions and selected tiebreaks in DB
  const handleSaveAndFinish = async () => {
    if (!torId) return;
    setLoading(true);
    try {
      const positions = standings.map((player) => ({
        jugadorId: player.jugadorId,
        posicionFinal: player.posicionFinal,
        puntajeFinal: player.puntos,
      }));

      await saveStandings(torId, {
        positions,
        tiebreaks: selectedTiebreaks,
      });

      setAlertInfo({
        open: true,
        message: 'Clasificación y criterios de desempate guardados correctamente.',
        severity: 'success',
      });

      // Redirect back to tournament list
      setTimeout(() => {
        navigate('/tournaments');
      }, 1500);
    } catch (error) {
      console.error('Error saving standings:', error);
      setAlertInfo({
        open: true,
        message: error.message || 'Error al guardar la clasificación final.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ color: '#cbd5e1', mb: 2, fontWeight: 600 }}>
        Paso 4: Cálculo de Puntos y Posiciones Finales
      </Typography>

      {/* Top Panel: Criterias Selection (Full Width) */}
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          background: 'rgba(15, 23, 42, 0.5)',
          borderColor: 'rgba(255, 255, 255, 0.08)',
          borderRadius: 0,
          mb: 3,
        }}
      >
        <Grid container spacing={3}>
          {/* Left Sub-column: Available Criterias */}
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="subtitle1" sx={{ color: '#cbd5e1', fontWeight: 600, mb: 1 }}>
                Criterios Disponibles
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
                Haz clic en un desempate para añadirlo al orden de aplicación. Pasa el cursor por el ícono de información para ver la descripción de cada uno.
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {dbTiebreaks.map((tb) => {
                  const isSelected = selectedTiebreaks.includes(tb.ttb_nombre);
                  return (
                    <Chip
                      key={tb.ttb_id}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {tb.ttb_nombre}
                          <Tooltip title={tb.ttb_descripcion || 'Sin descripción'} arrow placement="top">
                            <InfoIcon sx={{ fontSize: '14px', color: '#64748b', cursor: 'pointer', '&:hover': { color: '#cbd5e1' } }} />
                          </Tooltip>
                        </Box>
                      }
                      onClick={isSelected ? undefined : () => handleAddTiebreak(tb.ttb_nombre)}
                      disabled={isSelected}
                      icon={<AddIcon fontSize="small" />}
                      clickable={!isSelected}
                      sx={{
                        borderRadius: 0,
                        backgroundColor: isSelected ? 'rgba(51, 65, 85, 0.3)' : 'rgba(58, 110, 182, 0.1)',
                        color: isSelected ? '#475569' : '#cbd5e1',
                        border: '1px solid',
                        borderColor: isSelected ? 'transparent' : 'rgba(58, 110, 182, 0.3)',
                        '&:hover': isSelected ? {} : {
                          backgroundColor: 'rgba(58, 110, 182, 0.2)',
                          borderColor: '#3a6eb6',
                        },
                        '& .MuiChip-icon': {
                          color: isSelected ? '#475569' : '#3a6eb6',
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          </Grid>

          {/* Right Sub-column: Selected Criterias & Apply Button */}
          <Grid item xs={12} md={6} sx={{ borderLeft: { md: '1px solid rgba(255, 255, 255, 0.08)' }, pl: { md: 3 } }}>
            <Box>
              <Typography variant="subtitle1" sx={{ color: '#cbd5e1', fontWeight: 600, mb: 1 }}>
                Orden de Criterios Aplicados
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
                Define la prioridad de desempate de arriba hacia abajo usando las flechas.
              </Typography>

              {selectedTiebreaks.length === 0 ? (
                <Box
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    border: '1px dashed rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Ningún desempate seleccionado. Se ordenará únicamente por puntaje de partidas.
                  </Typography>
                </Box>
              ) : (
                <List dense sx={{ width: '100%', bgcolor: 'transparent', p: 0 }}>
                  {selectedTiebreaks.map((name, index) => {
                    const fullInfo = dbTiebreaks.find(d => d.ttb_nombre === name);
                    return (
                      <ListItem
                        key={name}
                        sx={{
                          mb: 1,
                          backgroundColor: 'rgba(15, 23, 42, 0.3)',
                          border: '1px solid rgba(255, 255, 255, 0.05)',
                          py: 0.5,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 35 }}>
                          <Chip
                            label={`${index + 1}°`}
                            size="small"
                            sx={{
                              borderRadius: 0,
                              backgroundColor: '#3a6eb6',
                              color: '#ffffff',
                              fontWeight: 'bold',
                              height: '20px',
                              fontSize: '11px',
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={name}
                          primaryTypographyProps={{ style: { color: '#cbd5e1', fontWeight: 500, fontSize: '14px' } }}
                          secondary={fullInfo ? fullInfo.ttb_descripcion : ''}
                          secondaryTypographyProps={{ style: { color: '#64748b', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' } }}
                        />
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            disabled={index === 0}
                            onClick={() => handleMoveTiebreakUp(index)}
                            sx={{ color: '#94a3b8', '&:disabled': { color: '#334155' } }}
                          >
                            <KeyboardArrowUpIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            disabled={index === selectedTiebreaks.length - 1}
                            onClick={() => handleMoveTiebreakDown(index)}
                            sx={{ color: '#94a3b8', '&:disabled': { color: '#334155' } }}
                          >
                            <KeyboardArrowDownIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveTiebreak(name)}
                            sx={{ color: '#ef4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              )}

              <Button
                fullWidth
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={handleApplyTiebreaks}
                disabled={standingsLoading}
                sx={{
                  mt: 2,
                  background: '#10b981',
                  borderRadius: 0,
                  '&:hover': { background: '#059669' },
                  '&:disabled': { background: 'rgba(16, 185, 129, 0.3)' }
                }}
              >
                {standingsLoading ? <CircularProgress size={20} color="inherit" /> : 'Aplicar Desempates'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Bottom Panel: Standings Table (Full Width) */}
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          background: 'rgba(15, 23, 42, 0.5)',
          borderColor: 'rgba(255, 255, 255, 0.08)',
          borderRadius: 0,
          minHeight: '320px',
          display: 'flex',
          flexDirection: 'column',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ color: '#cbd5e1', fontWeight: 600 }}>
            Clasificación General
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            * Usa las flechas en "Ajuste Manual" para forzar el puesto de un jugador.
          </Typography>
        </Box>

        {standingsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, py: 4 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : standings.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, py: 4 }}>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              No hay datos de clasificación disponibles. Asegúrate de tener jugadores y rondas cargadas.
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ border: '1px solid rgba(255, 255, 255, 0.05)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ py: 0.5, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)', width: '60px' }}>Puesto</TableCell>
                  <TableCell sx={{ py: 0.5, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>Jugador</TableCell>
                  <TableCell sx={{ py: 0.5, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)', width: '80px' }}>Puntos</TableCell>
                  {/* Render tiebreak headers for active filters */}
                  {selectedTiebreaks.map((tbName) => (
                    <TableCell
                      key={tbName}
                      sx={{
                        py: 0.5,
                        background: '#0f172a',
                        color: '#3a6eb6',
                        fontWeight: 'bold',
                        borderColor: 'rgba(255, 255, 255, 0.08)',
                        fontSize: '11px',
                        width: '90px',
                        textAlign: 'center',
                      }}
                    >
                      {tbName}
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ py: 0.5, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)', width: '100px' }}>Ajuste Manual</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {standings.map((player, idx) => (
                  <TableRow key={player.jugadorId} hover sx={{ '&:hover': { background: 'rgba(255, 255, 255, 0.02) !important' } }}>
                    <TableCell sx={{ py: 0.5, borderColor: 'rgba(255, 255, 255, 0.05)', fontWeight: 'bold', color: idx === 0 ? '#fbbf24' : idx === 1 ? '#cbd5e1' : idx === 2 ? '#d97706' : '#94a3b8' }}>
                      {player.posicionFinal ?? (idx + 1)}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, color: '#cbd5e1', fontWeight: 500, borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                      {player.apellidoNombre || `Jugador #${player.jugadorId}`}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, color: '#10b981', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                      {player.puntos}
                    </TableCell>
                    {/* Display each selected tiebreak score for this player */}
                    {selectedTiebreaks.map((tbName) => (
                      <TableCell
                        key={tbName}
                        sx={{
                          py: 0.5,
                          color: '#94a3b8',
                          borderColor: 'rgba(255, 255, 255, 0.05)',
                          fontSize: '13px',
                          width: '90px',
                          textAlign: 'center',
                        }}
                      >
                        {player.tiebreaks?.[tbName] !== undefined ? player.tiebreaks[tbName] : '0'}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ py: 0.5, borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                      <IconButton
                        size="small"
                        disabled={idx === 0}
                        onClick={() => handleMovePlayerUp(idx)}
                        sx={{ p: 0.25, color: '#3a6eb6', '&:disabled': { color: '#334155' } }}
                      >
                        <KeyboardArrowUpIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={idx === standings.length - 1}
                        onClick={() => handleMovePlayerDown(idx)}
                        sx={{ p: 0.25, color: '#3a6eb6', '&:disabled': { color: '#334155' } }}
                      >
                        <KeyboardArrowDownIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Step Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          disabled={loading}
          sx={{
            color: '#94a3b8',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: 0,
            '&:hover': { borderColor: '#cbd5e1', background: 'rgba(255, 255, 255, 0.05)' },
          }}
        >
          Atrás
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSaveAndFinish}
          disabled={loading || standings.length === 0}
          sx={{
            background: '#3a6eb6',
            borderRadius: 0,
            '&:hover': { background: '#2a5793' },
            '&:disabled': { background: 'rgba(58, 110, 182, 0.3)' }
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Guardar y Finalizar'}
        </Button>
      </Box>
    </Box>
  );
}
